import React from "react";
import Select from "react-select";

interface GenericDropdownProps {
  options: { value: string; label: string }[]; // Dropdown options
  value: { value: string; label: string }[]; // Selected values for multi-select
  onChange: (selectedOption: any) => void; // Function to handle changes
  isMulti?: boolean; // Support multi-select
  placeholder?: string; // Placeholder text
}

const GenericDropdown: React.FC<GenericDropdownProps> = ({
  options,
  value,
  onChange,
  isMulti = true,
  placeholder = "Select an option",
}) => {
  // Custom styles for react-select
  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      backgroundColor: "transparent", // Slate
      border: "1px solid #99CCFF", // Light blue border
      borderRadius: "4px",
      padding: "4px",
      fontSize: "14px",
      color: "#AFC3DA", // Dark grey
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#AFC3DA", // Dark grey
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: "#232833", // Dark grey
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: "#031545", // White
      border: "1px solid #99CCFF", // Light blue border
      borderRadius: "4px",
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#2666B3" // Slate for selected
        : state.isFocused
        ? "#AFC3DA" // Light grey on hover
        : "#212D44", // White for unselected
      color: "#AFC3DA", // Dark slate for selected text
      ":hover": {
        backgroundColor: "#E8E9EC", // Light grey on hover
        color: "#13161B", // Dark text on hover
      },
    }),
  };

  const MultiValue = ({ data, index }: any) => {
    const isOverflow = index === 5; // Truncate after 5 items
    const displayText = isOverflow ? "" : data.label;

    return isOverflow ? (
      <span style={{ fontWeight: "bold", color: "#AFC3DA" }}>
        , ... {displayText}
      </span>
    ) : index < 5 ? (
      <span>{index > 0 ? `, ${displayText}` : displayText}</span>
    ) : null;
  };

  const handleSelectChange = (selected: any) => {
    if (Array.isArray(selected)) {
      const isSelectAllSelected = selected.some(
        (item) => item.value === "select-all"
      );

      if (isSelectAllSelected) {
        if (value.length === options.length) {
          // If already selected all, deselect everything
          onChange([]);
        } else {
          // Select all options
          onChange(options);
        }
      } else {
        // Handle normal selection
        onChange(selected);
      }
    }
  };

  // Add "Select All" option dynamically
  const optionsWithSelectAll = [
    { value: "select-all", label: "Select All" },
    ...options,
  ];

  return (
    <Select
      options={optionsWithSelectAll}
      value={value}
      onChange={handleSelectChange}
      isMulti={isMulti}
      placeholder={placeholder}
      styles={customStyles}
      components={{ MultiValue }}
    />
  );
};

export default GenericDropdown;
